import React, { useContext, useState } from 'react';
import { useTranslation } from 'Shared/contexts/TranslationsContext';
import classnames from 'classnames';
import { useHistory, matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

// Types
import { Feature } from 'Shared/types/Organization.type';

// Constants
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';
import { ESSENTIALS_TOOLTIP } from 'Shared/constants/PerformEssentials.constants';

// Hooks
import { useFeature } from 'Shared/utils/hooks';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useSmac } from 'Shared/utils/hooks/use-smac';
import { useTooltip } from 'Wpds/components/tooltip/useTooltip';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Context
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';
import WpTooltipOverlay from 'Wpds/components/tooltip/WpTooltipOverlay';
import BetaBadge from 'Shared/components/BetaBadge/BetaBadge';

type Props = {
  handleCloseNavbar: Function;
};

const ReviewsMenuItem = ({ handleCloseNavbar }: Props) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const tooltip = useTooltip({ placement: 'right' });

  const isExpanded = useContext(ExpandNavbarContext);

  const { isOrgAdmin } = useCurrentUser();
  const { isFreePlan, isFreeTrial } = useFeature(Feature.performanceReviews);
  const { isPiDemoAccount } = useOrganization();
  const { isPartnerAccount } = useSmac();
  const [isOpen, setIsOpen] = useState(false);

  const isLocked = isFreePlan || isFreeTrial || isPartnerAccount;
  const { tr } = useTranslation();

  const isActiveRoute = matchPath(pathname, { path: [PageUrlsConstants.PERFORMANCE_REVIEWS] });

  const handleCloseSidebarFn = () => {
    if (isExpanded) handleCloseNavbar();
  };

  const handleReviewsClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.PERFORMANCE_REVIEWS);
  };

  const handleReviewAdministrationClick = () => {
    handleCloseSidebarFn();
    history.push(PageUrlsConstants.PERFORMANCE_REVIEWS_ADMIN);
  };

  const itemTemplate = (
    <div
      className={classnames('nav-link d-flex align-items-center cursor-pointer', { active: isOpen || isActiveRoute })}
      ref={tooltip.targetRef}
    >
      <div
        className={classnames('side-navigation-icon position-relative', {
          'mx-auto': !isExpanded,
        })}
      >
        <WpIcon name="file-signature" />
      </div>
      {isExpanded && (
        <>
          <div className="d-inline">{tr('perform.side_navigation.reviews')}</div>
          <div className="ml-2">
            <BetaBadge />
          </div>
          <div className="d-inline ml-auto mr-1">{isOrgAdmin && !isFreePlan && <WpIcon name="chevron-right" />}</div>
          {isLocked && (
            <div className="d-inline">
              <WpIcon name="lock-alt" />
            </div>
          )}
        </>
      )}
    </div>
  );

  if (isPiDemoAccount) return null;

  return (
    <>
      {!isOrgAdmin || isFreePlan ? (
        <NavLink to={PageUrlsConstants.PERFORMANCE_REVIEWS} onClick={handleCloseSidebarFn}>
          {itemTemplate}
        </NavLink>
      ) : (
        <WpDropdown placement="right-start" isOpen={isOpen} setOpen={setIsOpen} withBoxShadow>
          <WpDropdownToggle>
            <li className="Notifications nav-item">{itemTemplate}</li>
          </WpDropdownToggle>
          <WpDropdownMenu fitHeight>
            <WpDropdownMenuItem onClick={handleReviewsClick}>
              <WpIcon name="file-signature" /> {tr('perform.side_navigation.reviews')}
            </WpDropdownMenuItem>
            <WpDropdownMenuItem onClick={handleReviewAdministrationClick}>
              <WpIcon name="cog" /> {tr('perform.side_navigation.reviews_admin')}
            </WpDropdownMenuItem>
          </WpDropdownMenu>
        </WpDropdown>
      )}
      {!isExpanded && !isOpen && (
        <WpTooltipOverlay context={tooltip}>{tr('perform.side_navigation.reviews')}</WpTooltipOverlay>
      )}
      {isExpanded && isLocked && !isOpen && <WpTooltipOverlay context={tooltip}>{ESSENTIALS_TOOLTIP}</WpTooltipOverlay>}
    </>
  );
};

export default ReviewsMenuItem;
